// rechenbeispiel — Kennzahlen eines Modellfalls, gross gesetzt.
//
// In Marianns Ratgebern ist das die Stelle, an der aus einem abstrakten Modell
// eine Hausnummer wird: "Auszahlung 100.000 €, Nutzungsentgelt 5.750 € im
// Jahr". Vier bis fuenf solcher Zahlen pro Seite, plus eine Ergebniszeile.
//
// Das Hochzaehlen stammt aus dem 420Pharma-Projekt und traegt dessen wichtigste
// Eigenschaft mit: die fertige Zahl steht sofort im Markup und wird nur
// ueberschrieben, solange die Animation laeuft. Faellt JavaScript aus, sieht
// niemand eine Null. "Reduzierte Bewegung" wird respektiert.
//
// Einstellungen am Wrapper:
//   data-titel        Ueberschrift
//   data-ergebnis     Ergebniszeile, optisch abgesetzt: "Bezeichnung | Wert | Zusatz"
//   data-hochzaehlen  "true" (Standard) | "false"
//   data-hinweis      kleine Zeile unter dem Kasten
import { el, haenge, leerHinweis, lesSchalter } from '@lib/bausteine/dom';
import { formatiere, zerlegeWert, zerlegeZeilen } from '@lib/bausteine/zerlege';
import { hochzaehlen } from '@lib/bausteine/hochzaehlen';
import { zeichneAus } from '@lib/bausteine/auszeichnung';

/**
 * Eine einzelne Zahl. Animiert werden darf nur der Zahlenanteil — Waehrung und
 * Zusaetze wie "ca." muessen stehen bleiben, deshalb bekommt die Zahl ihren
 * eigenen Span.
 */
function zahl(roh: string, animieren: boolean): HTMLElement {
  const wert = zerlegeWert(roh);
  const aussen = el('span', { class: 'eoe-rechnung__wert' });

  // Kein Zahlenanteil im Text (z. B. "auf Anfrage"): unveraendert ausgeben.
  if (wert.zahl === null) {
    aussen.textContent = wert.roh;
    return aussen;
  }

  const zaehler = el('span', null, formatiere(wert.zahl, wert.dezimalstellen));
  haenge(aussen, [wert.praefix, zaehler, wert.suffix]);

  if (animieren) hochzaehlen(zaehler, wert.zahl, wert.dezimalstellen);
  return aussen;
}

/** Eine Zeile "Bezeichnung | Wert | Erlaeuterung" als Wert-Element mit Zusatz. */
function wertMitErlaeuterung(
  wertText: string,
  erlaeuterung: string | undefined,
  animieren: boolean,
): Node[] {
  return [
    zahl(wertText, animieren),
    erlaeuterung
      ? el('span', { class: 'eoe-rechnung__erlaeuterung' }, zeichneAus(erlaeuterung))
      : null,
  ].filter((knoten): knoten is HTMLElement => knoten !== null);
}

export function rechenbeispiel(wurzel: HTMLElement, quelle: string): Node {
  const posten = zerlegeZeilen(quelle);
  const abschluss = zerlegeZeilen(wurzel.getAttribute('data-ergebnis'));

  if (posten.length === 0 && abschluss.length === 0) {
    return leerHinweis('Noch keine Kennzahlen. Format: Bezeichnung | Wert | Erläuterung');
  }

  const titel = (wurzel.getAttribute('data-titel') ?? '').trim();
  const animieren = lesSchalter(wurzel, 'data-hochzaehlen', true);
  const hinweis = (wurzel.getAttribute('data-hinweis') ?? '').trim();

  return el(
    'section',
    { class: 'eoe-reset eoe-rechnung' },
    titel && el('h3', { class: 'eoe-titel' }, titel),

    posten.length > 0 &&
      el(
        'dl',
        { class: 'eoe-rechnung__gitter' },
        ...posten.map((eintrag) => {
          const [bezeichnung, wert, erlaeuterung] = eintrag.felder;
          return el(
            'div',
            { class: 'eoe-rechnung__posten' },
            el('dt', { class: 'eoe-rechnung__bezeichnung' }, zeichneAus(bezeichnung ?? '')),
            el(
              'dd',
              { class: 'eoe-rechnung__zahl' },
              ...wertMitErlaeuterung(wert ?? '', erlaeuterung, animieren),
            ),
          );
        }),
      ),

    abschluss.length > 0 &&
      el(
        'div',
        { class: 'eoe-rechnung__ergebnis' },
        ...abschluss.map((eintrag) => {
          const [bezeichnung, wert, erlaeuterung] = eintrag.felder;
          return el(
            'p',
            { class: 'eoe-rechnung__ergebniszeile' },
            el('span', { class: 'eoe-rechnung__ergebnistext' }, zeichneAus(bezeichnung ?? '')),
            el(
              'span',
              { class: 'eoe-rechnung__ergebniswert' },
              ...wertMitErlaeuterung(wert ?? '', erlaeuterung, animieren),
            ),
          );
        }),
      ),

    hinweis && el('p', { class: 'eoe-fussnote eoe-rechnung__hinweis' }, zeichneAus(hinweis)),
  );
}
